/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

console.log("custom.js loaded");

function initShopTheLook() {
  const checkboxes = document.querySelectorAll(".stl-add-checkbox");
  const selects = document.querySelectorAll(".stl-variant-select");
  const totalPriceEl = document.getElementById("stl-total-price");
  const addAllBtn = document.getElementById("stl-add-all");

  if (!checkboxes.length || !selects.length || !totalPriceEl || !addAllBtn) {
    console.warn("Shop The Look: Elements not found");
    return;
  }

  console.log("Shop The Look: Initialised");

  // -------------------------------
  // Update total price + button state
  // -------------------------------
  function updateTotal() {
    let total = 0;
    let selectedCount = 0;

    checkboxes.forEach((cb) => {
        if (!cb) return;                 // ← bulletproof
        if (cb.disabled) return;         // skip sold‑out
        if (!cb.dataset) return;         // ← bulletproof
        if (!cb.dataset.productId) return;

        if (cb.checked) {
        selectedCount++;

        const productId = cb.dataset.productId;
        const select = document.querySelector(
            `.stl-variant-select[data-product-id="${productId}"]`
        );

        if (select && select.selectedOptions.length) {
            const price = Number(select.selectedOptions[0].dataset.price || 0);
            total += price;
        }
        }
    });

    totalPriceEl.textContent = `£${(total / 100).toFixed(2)}`;

    if (selectedCount > 0) {
        addAllBtn.removeAttribute("disabled");
        addAllBtn.classList.add("enabled");
    } else {
        addAllBtn.setAttribute("disabled", "disabled");
        addAllBtn.classList.remove("enabled");
    }
  }

  // -------------------------------
  // Add items to cart (one-by-one)
  // -------------------------------
  async function addAllToCart() {
  // ⭐ Start loading state
  addAllBtn.classList.add("loading");
  addAllBtn.setAttribute("disabled", "disabled");

  const items = [];

  checkboxes.forEach((cb) => {
    if (cb.checked) {
    const productId = cb.dataset.productId;
    const select = document.querySelector(
        `.stl-variant-select[data-product-id="${productId}"]`
    );

    if (select) {
        items.push({
        id: Number(select.value.trim()),
        quantity: 1,
        });
    }
    }
  });

  if (!items.length) return;

  for (const item of items) {
    const res = await fetch("/cart/add.js", {
    method: "POST",
    headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
    },
    body: JSON.stringify(item),
    });

    if (!res.ok) {
    console.error("Failed to add item:", item, await res.text());
    return;
    }
  }

  // ⭐ Refresh drawer contents
  document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));

  // ⭐ Open drawer
  document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));

  // ⭐ End loading state
  addAllBtn.classList.remove("loading");
  addAllBtn.removeAttribute("disabled");
  }

  // -------------------------------
  // Event listeners
  // -------------------------------
  checkboxes.forEach((cb) => cb.addEventListener("change", updateTotal));
  selects.forEach((sel) => sel.addEventListener("change", updateTotal));
  addAllBtn.addEventListener("click", addAllToCart);

  // Initial render
  updateTotal();
}

// Run after full page load
window.addEventListener("load", () => {
  setTimeout(initShopTheLook, 50);
});

// -------------------------------
// Dispatch countdown
// -------------------------------
// Bulletproof UK time helper
function getUKNow() {
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false
  });

  const parts = formatter.formatToParts(new Date());
  const map = {};
  parts.forEach(p => (map[p.type] = p.value));

  return new Date(
    map.year,
    map.month - 1,
    map.day,
    map.hour,
    map.minute,
    map.second
  );
}

document.addEventListener("DOMContentLoaded", function () {
  function getDispatchHTML() {
    const ukNow = getUKNow();

    const day = ukNow.getDay(); // 0 = Sun, 6 = Sat
    const isWeekend = day === 0 || day === 6;

    const cutoff = new Date(
      ukNow.getFullYear(),
      ukNow.getMonth(),
      ukNow.getDate(),
      14, 0, 0, 0
    ); // 2pm UK today

    const isBeforeCutoff = ukNow < cutoff;

    let countdownText = "";
    let triggerText = "";

    // Determine next dispatch day label
    let dispatchDayText;
    if (isWeekend) {
      dispatchDayText = "Monday";
    } else if (isBeforeCutoff) {
      dispatchDayText = "today";
    } else {
      dispatchDayText = day === 5 ? "Monday" : "tomorrow"; // Friday → Monday
    }

    // Smart trigger
    if (isWeekend) {
      triggerText = "Order now for Monday dispatch";
    } else if (isBeforeCutoff) {
      triggerText = "Order before 2pm for same-day dispatch";
    } else {
      triggerText = "Order now for next working day dispatch";
    }

    // Countdown / status
    if (isWeekend) {
      countdownText = "It's the weekend, dispatches resume Monday";
    } else if (isBeforeCutoff) {
      const diffMs = cutoff - ukNow;
      const diffH = Math.floor(diffMs / (1000 * 60 * 60));
      const diffM = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const diffS = Math.floor((diffMs % (1000 * 60)) / 1000);

      countdownText = `Order within ${diffH}h ${diffM}m ${diffS}s for dispatch today`;
    } else {
      countdownText = `Order cut-off now passed, dispatches resume ${dispatchDayText}`;
    }

    return `
      <p>${countdownText}</p>
      <p>Delivered in 1–3 working days (UK)</p>
      <p>${triggerText}</p>
    `;
  }

  const productEl = document.getElementById("shipping-messaging");
  const drawerEl = document.getElementById("cart-shipping-messaging");

  function renderDispatchMessaging() {
    const html = getDispatchHTML();
    if (productEl) productEl.innerHTML = html;
    if (drawerEl) drawerEl.innerHTML = html;
  }

  // Initial render
  renderDispatchMessaging();

  // Live countdown (every second)
  setInterval(renderDispatchMessaging, 1000);

  // If your theme fires an event when cart drawer opens, hook it here
  document.addEventListener("dispatch:cart-drawer:open", renderDispatchMessaging);
});
